import React, { FC, useCallback, useState } from 'react';
import useDealTypes from './hooks/useDealTypes';
import Card from 'src/components/Card';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@material-ui/core';
import { MdClear as ClearIcon } from '@react-icons/all-files/md/MdClear';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { formatDate, momentDate } from './utils';
import MomentUtils from '@date-io/moment';
import Button from 'src/components/Button';
import useOrigins from './hooks/useOrigins';
import LegacyCustomerAutocomplete from 'src/components/ApiField/LegacyCustomerAutocomplete';
import LegacyEmployeeSelect from 'src/components/ApiField/LegacyEmployeeSelect';
import useLossReasons from './hooks/useLossReasons';
import { StepNumbersOptions } from '../../../../interfaces/api/Deal';
import LegacyDomainSelect from '../../../../components/ApiField/LegacyDomainSelect';

const stepNumberOperators = ['Fait partie de', 'Ne fait pas partie de'];

export interface GetDealsExportResponseBody {
  dateCreateStart: string;
  dateCreateEnd: string;
  dateCreateOperator: string;
  dateClosedStart: string;
  dateClosedEnd: string;
  dateClosedOperator: string;
  stepNumberOperator: string;
  stepNumber: string;
  lost: boolean;
  won: boolean;
  standby: boolean;
  origins: number[];
  types: number[];
  customerId: number;
  domainId: number;
  lossReasons: number[];
  employeeCommercials: number[];
  creatorIds: number[];
}

interface Props {
  onValidate: (data: GetDealsExportResponseBody) => void;
}

const Deals: FC<Props> = ({ onValidate }) => {
  const [formError, setFormError] = useState<string>();
  const lossReasons = useLossReasons();
  const types = useDealTypes();
  const origins = useOrigins();

  const {
    watch,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<GetDealsExportResponseBody>({
    defaultValues: {
      stepNumberOperator: stepNumberOperators[0],
      origins: [],
      creatorIds: [],
      employeeCommercials: [],
      lossReasons: [],
      types: [],
    },
  });

  const originsWatch = watch('origins');
  const typesWatch = watch('types');
  const lossReasonsWatch = watch('lossReasons');

  const onSubmit = useCallback(
    async (data: GetDealsExportResponseBody) => {
      try {
        setFormError(undefined);
        await onValidate(data);
      } catch (e) {
        setFormError(e.message);
      }
    },
    [onValidate]
  );

  const renderOriginValue = useCallback(
    (selected: unknown) =>
      origins
        .filter((origin) => (selected as number[]).includes(origin.value))
        .map((origin) => origin.label)
        .join(', '),
    [origins]
  );

  const renderTypeValue = useCallback(
    (selected: unknown) =>
      types
        .filter((type) => (selected as number[]).includes(type.value))
        .map((type) => type.label)
        .join(', '),
    [types]
  );

  const renderLossReasonValue = useCallback(
    (selected: unknown) =>
      lossReasons
        .filter((lossReason) => (selected as number[]).includes(lossReason.value))
        .map((lossReason) => lossReason.label)
        .join(', '),
    [lossReasons]
  );

  const renderStepOperatorValue = useCallback((selected: unknown) => (selected as string[]).join(', '), []);

  const handleClearDate = useCallback(
    (onChange) => (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(undefined);
    },
    []
  );

  return (
    <Card>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant='h6'>Paramètres d'export</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='customerId'
              control={control}
              render={({ field: { ref, ...field } }) => <LegacyCustomerAutocomplete size='small' {...field} />}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='stepNumber'
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <FormControl variant='outlined' size='small' fullWidth>
                  <InputLabel>Étape</InputLabel>
                  <Select native value={value || ''} {...field} input={<OutlinedInput label='Étape' />}>
                    <option value='' />
                    {StepNumbersOptions.map((stepNumber) => (
                      <option key={stepNumber.value} value={stepNumber.value}>
                        {stepNumber.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name='lost'
              control={control}
              render={({ field: { value, ref, ...field }, fieldState: { error } }) => (
                <FormControlLabel
                  control={<Checkbox size='small' />}
                  label='Perdu'
                  checked={value || false}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name='won'
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <FormControlLabel
                  control={<Checkbox size='small' />}
                  label='Gagné'
                  checked={value || false}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name='standby'
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <FormControlLabel
                  control={<Checkbox size='small' />}
                  label='Stand-by'
                  checked={value || false}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='body1'>Date de création</Typography>
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='dateCreateStart'
              control={control}
              render={({ field: { value, onChange, ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  label='Date de début'
                  format='DD/MM/YYYY'
                  inputVariant='outlined'
                  size='small'
                  fullWidth
                  helperText={error?.message}
                  error={undefined !== error}
                  value={momentDate(value)}
                  onChange={useCallback((date) => onChange(formatDate(date)), [onChange])}
                  InputProps={{
                    endAdornment: value && (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleClearDate(onChange)}>
                          <ClearIcon color='disabled' fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='dateCreateEnd'
              control={control}
              render={({ field: { value, onChange, ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  label='Date de fin'
                  format='DD/MM/YYYY'
                  inputVariant='outlined'
                  size='small'
                  fullWidth
                  helperText={error?.message}
                  error={undefined !== error}
                  value={momentDate(value)}
                  onChange={useCallback((date) => onChange(formatDate(date)), [onChange])}
                  InputProps={{
                    endAdornment: value && (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleClearDate(onChange)}>
                          <ClearIcon color='disabled' fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='body1'>Date de cloture</Typography>
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='dateClosedStart'
              control={control}
              render={({ field: { value, onChange, ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  label='Date de début'
                  format='DD/MM/YYYY'
                  inputVariant='outlined'
                  size='small'
                  fullWidth
                  helperText={error?.message}
                  error={undefined !== error}
                  value={momentDate(value)}
                  onChange={useCallback((date) => onChange(formatDate(date)), [onChange])}
                  InputProps={{
                    endAdornment: value && (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleClearDate(onChange)}>
                          <ClearIcon color='disabled' fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='dateClosedEnd'
              control={control}
              render={({ field: { value, onChange, ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  label='Date de fin'
                  format='DD/MM/YYYY'
                  inputVariant='outlined'
                  size='small'
                  fullWidth
                  helperText={error?.message}
                  error={undefined !== error}
                  value={momentDate(value)}
                  onChange={useCallback((date) => onChange(formatDate(date)), [onChange])}
                  InputProps={{
                    endAdornment: value && (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleClearDate(onChange)}>
                          <ClearIcon color='disabled' fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='origins'
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <FormControl size='small' variant='outlined' fullWidth>
                  <InputLabel>Origine</InputLabel>
                  <Select
                    multiple
                    input={<OutlinedInput label='Origine' />}
                    value={value || []}
                    renderValue={renderOriginValue}
                    {...field}
                  >
                    {origins.map((origin) => (
                      <MenuItem key={origin.value} value={origin.value}>
                        <Checkbox checked={originsWatch.includes(origin.value)} />
                        <ListItemText primary={origin.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='types'
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <FormControl size='small' variant='outlined' fullWidth>
                  <InputLabel>Type d'affaire</InputLabel>
                  <Select
                    multiple
                    input={<OutlinedInput label="Type d'affaire" />}
                    value={value || []}
                    renderValue={renderTypeValue}
                    {...field}
                  >
                    {types.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Checkbox checked={typesWatch.includes(type.value)} />
                        <ListItemText primary={type.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='lossReasons'
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <FormControl variant='outlined' size='small' fullWidth>
                  <InputLabel>Raison de perte</InputLabel>
                  <Select
                    multiple
                    input={<OutlinedInput label='Raison de perte' />}
                    value={value || []}
                    renderValue={renderLossReasonValue}
                    {...field}
                  >
                    {lossReasons.map((lossReason) => (
                      <MenuItem key={lossReason.value} value={lossReason.value}>
                        <Checkbox checked={lossReasonsWatch.includes(lossReason.value)} />
                        <ListItemText primary={lossReason.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='employeeCommercials'
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <LegacyEmployeeSelect
                  {...field}
                  label='Commercial en charge'
                  multiple
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='creatorIds'
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <LegacyEmployeeSelect
                  {...field}
                  label='Créateur'
                  multiple
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name='domainId'
              control={control}
              render={({ field: { ref, value, ...field } }) => <LegacyDomainSelect value={value} {...field} />}
            />
          </Grid>

          {formError && (
            <Grid item xs={12}>
              <Box color='red'>
                <em>{formError}</em>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display='flex' justifyContent='flex-end'>
              <Button color='primary' variant='contained' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
                Exporter les affaires
              </Button>
            </Box>
          </Grid>
        </Grid>
      </MuiPickersUtilsProvider>
    </Card>
  );
};

export default Deals;
